export enum ROLE {
  ManageProducts = "ManageProducts",
  ConfirmProducts = "ConfirmProducts",
  ManageTransfer = "ManageTransfers",
  MPIAgent = "MPIAgent",
  Forbidden = "Forbidden", // used for testing unauthorized access, will remove later
  InternalReadOnly = "InternalReadOnly",
  ExternalReadOnly = "ExternalReadOnly",
  EligibilitySubmitter = "EligibilitySubmitter",
  ManageExports = "ManageExports",
  AccessOfficialAssurance = "AccessOfficialAssurance",
  OCABorder = "OCABorder",
}

export enum PERMISSION_CODE {
  EXPORT_REQUEST_VIEW = "export_requests:view",
  EXPORT_REQUEST_EDIT = "export_requests:edit",
  TRANSFER_REQUEST_EDIT = "transfer_requests:edit",
  EXPORT_REQUEST_MPI_REPRESENT = "export_requests:mpi_represent",

  PRODUCT_VIEW = "products:view",
  PRODUCT_EDIT = "products:edit",
  PRODUCT_CONFIRM = "products:confirm",
  PRODUCT_ADD = "products:add",
  PRODUCT_DOWNLOAD = "products:download",

  PRODUCT_PACKAGING_RUN_EDIT = "product:packaging_run:edit",

  PRODUCT_MPI_EDIT = "products:mpi_edit",

  REPLACEMENT_EDIT = "replacement:create",
  REPLACEMENT_CANCEL = "replacement:cancel",

  // Transfer permissions
  TRANSFER_REQUEST_CREATE = "transfer:request:create",
  TRANSFER_REQUEST_CREATE_FROM_EXISTING = "transfer:request:createFromExisting",
  TRANSFER_REQUEST_READ = "transfer:request:read",
  TRANSFER_REQUEST_UPDATE = "transfer:request:update",
  TRANSFER_REQUEST_SUBMIT = "transfer:request:submit",
  TRANSFER_REQUEST_COPY = "transfer:request:copy",
  TRANSFER_REQUEST_CONFIGURE_AUTO_APPROVE = "transfer:request:configureAutoApprove",

  TRANSFER_PRODUCT_CREATE = "transfer:product:create",
  TRANSFER_PRODUCT_CREATE_FROM_SOURCE = "transfer:product:createFromSource",
  TRANSFER_PRODUCT_READ = "transfer:product:read",
  TRANSFER_PRODUCT_UPDATE = "transfer:product:update",
  TRANSFER_PRODUCT_DELETE = "transfer:product:delete",
  TRANSFER_PRODUCT_COPY = "transfer:product:copy",
  TRANSFER_PRODUCT_DECLARE_ELIGIBILITY = "transfer:product:declareEligibility",

  TRANSFER_PROCESS_CREATE = "transfer:process:create",
  TRANSFER_PROCESS_READ = "transfer:process:read",
  TRANSFER_PROCESS_UPDATE = "transfer:process:update",
  TRANSFER_PROCESS_DELETE = "transfer:process:delete",
  TRANSFER_PROCESS_COPY = "transfer:process:copy",

  // Transfer party view permissions
  TRANSFER_CONSIGNOR_VIEW_INFO = "transfer:consignor:viewInfo",
  TRANSFER_CONSIGNEE_VIEW_INFO = "transfer:consignee:viewInfo",
  TRANSFER_THIRD_PARTY_VIEW_INFO = "transfer:thirdParty:viewInfo",

  // Export permissions
  EXPORT_REQUEST_CREATE = "export:request:create",
  EXPORT_REQUEST_READ = "export:request:read",
  EXPORT_REQUEST_UPDATE = "export:request:update",
  EXPORT_REQUEST_LIST = "export:request:list",

  EXPORT_REQUEST_SUBMIT = "export:request:submit",
  EXPORT_REQUEST_COPY = "export:request:copy",

  EXPORT_TEMPLATE_SEARCH = "export:template:search",

  EXPORT_PRODUCT_CREATE = "export:product:create",
  EXPORT_PRODUCT_READ = "export:product:read",
  EXPORT_PRODUCT_UPDATE = "export:product:update",
  EXPORT_PRODUCT_DELETE = "export:product:delete",
  EXPORT_PRODUCT_COPY = "export:product:copy",

  EXPORT_PROCESS_CREATE = "export:process:create",
  EXPORT_PROCESS_READ = "export:process:read",
  EXPORT_PROCESS_UPDATE = "export:process:update",
  EXPORT_PROCESS_DELETE = "export:process:delete",
  EXPORT_PROCESS_COPY = "export:process:copy",

  OFFICIAL_ASSURANCE_PRINT = "official_assurance:print",

  // Supporting Documents permissions
  SUPPORTING_DOCUMENT_VIEW = "supporting_document:view",
  SUPPORTING_DOCUMENT_CREATE = "supporting_document:create",
}

export enum FEATURE_FLAGS {
  ESTAR_EIP_POLLING = "estarEipPolling",
  FEATURE_FLAG_CIP_6_2 = "ff_cip_6_2",
  FEATURE_FLAG_CIP_178802_WINE_IMPROVE_EC_FILTERS = "ff_cip_178802_wine_improve_ec_filters",
  FEATURE_FLAG_WINE_REPLACEMENT_REASON_CATEGORIES = "ff_cip_178801_wine_replacement_reason_categories",
  FEATURE_FLAG_PRODUCT_SUMMARY = "ff_cip_182726_product_summary",
}

export const ApiProductPermissionErrorMessage = "Action only allowed for own"

type FallbackPath = Record<string, string>
export interface Route {
  label: string
  pathname: RegExp
  path: string
  access?: ROLE[]
  featureFlags?: FEATURE_FLAGS[]
  ffPath?: FallbackPath
  pageLabel?: string
  isModal?: boolean
}
/**
 * access: the roles that can access the route, if not provided, all roles can access the route
 * @readonly
 */
export const dfeRoutes: readonly Route[] = Object.freeze([
  {
    label: "Home",
    pathname: new RegExp(/^\/$/),
    path: "/",
    access: [
      ROLE.ManageProducts,
      ROLE.MPIAgent,
      ROLE.InternalReadOnly,
      ROLE.ExternalReadOnly,
      ROLE.ManageExports,
      ROLE.ManageTransfer,
      ROLE.EligibilitySubmitter,
      ROLE.AccessOfficialAssurance,
      ROLE.ConfirmProducts,
    ],
  },
  {
    label: "Export request",
    pathname: new RegExp(/^(\/(export-request|request\/export)(\/.*)?|\/)?$/),
    path: "/request/export/start",
    access: [ROLE.ManageProducts, ROLE.MPIAgent, ROLE.ManageExports],
    pageLabel: "Start export request",
  },
  {
    label: "View all requests",
    pathname: new RegExp(/^(\/(requests)(\/.*)?|\/)?$/),
    path: "/requests",
    access: [
      ROLE.ManageProducts,
      ROLE.MPIAgent,
      ROLE.InternalReadOnly,
      ROLE.ExternalReadOnly,
      ROLE.ManageExports,
      ROLE.OCABorder,
    ],
  },
  {
    label: "View all transfers",
    pathname: new RegExp(/^(\/(transfer-requests)(\/.*)?|\/)?$/),
    path: "/transfer-requests",
    access: [
      ROLE.ManageTransfer,
      ROLE.MPIAgent,
      ROLE.InternalReadOnly,
      ROLE.ExternalReadOnly,
      ROLE.EligibilitySubmitter,
    ],
  },
  {
    label: "View all products",
    pathname: new RegExp(/^(\/(products)(\/.*)?|\/)?$/),
    path: "/products",
    access: [
      ROLE.ManageProducts,
      ROLE.ConfirmProducts,
      ROLE.MPIAgent,
      ROLE.InternalReadOnly,
      ROLE.ExternalReadOnly,
    ],
  },
  {
    label: "Supporting documents",
    pathname: new RegExp(/^(\/(supporting-documents)(\/.*)?|\/)?$/),
    path: "/supporting-documents",
    access: [
      ROLE.ManageExports,
      ROLE.ManageTransfer,
      ROLE.MPIAgent,
      ROLE.ExternalReadOnly,
      ROLE.InternalReadOnly,
    ],
    pageLabel: "View all supporting documents",
  },

  {
    label: "Start transfer",
    pathname: new RegExp(/^(\/(request\/transfer)(\/.*)?|\/)?$/),
    path: "/request/transfer/start",
    access: [ROLE.MPIAgent, ROLE.ManageTransfer],
  },
  {
    label: "XML submit",
    pathname: new RegExp(/^(\/(xml-submit)(\/.*)?|\/)?$/),
    path: "/xml-submit",
    access: [
      ROLE.ManageTransfer,
      ROLE.MPIAgent,
      ROLE.ManageExports,
      ROLE.ExternalReadOnly,
    ],
    isModal: true,
  },
])

export const dfeAccessRoutes: readonly Route[] = Object.freeze([
  {
    label: "Terms and Conditions",
    pathname: new RegExp(/^(\/(terms-and-conditions)(\/.*)?|\/)?$/),
    path: "/terms-and-conditions",
    access: [
      ROLE.ManageProducts,
      ROLE.ConfirmProducts,
      ROLE.MPIAgent,
      ROLE.InternalReadOnly,
      ROLE.ExternalReadOnly,
      ROLE.ManageExports,
      ROLE.EligibilitySubmitter,
      ROLE.OCABorder,
    ],
  },
  {
    label: "View transfer request",
    pathname: new RegExp(/^\/request\/transfer\/(?!start)[^/]+(\/.*)?\/?\/?$/),
    path: "/request/transfer",
    access: [
      ROLE.ManageTransfer,
      ROLE.MPIAgent,
      ROLE.InternalReadOnly,

      ROLE.ExternalReadOnly,
    ],
  },
  {
    label: "View export request",
    pathname: new RegExp(/^\/request\/export\/(?!start)[^/]+(\/.*)?\/?\/?$/),
    path: "/request/export",
    access: [ROLE.ExternalReadOnly, ROLE.InternalReadOnly],
  },
  {
    label: "View export request (legacy)",
    pathname: new RegExp(/^\/export-request\/(?!start)[^/]+(\/.*)?\/?\/?$/),
    path: "/export-request",
    access: [ROLE.ExternalReadOnly, ROLE.InternalReadOnly],
  },
  {
    label: "Extract view",
    pathname: new RegExp(/^\/extract\/[^/]+\/?$/),
    path: "/extract",
    access: [
      ROLE.ManageExports,
      ROLE.MPIAgent,
      ROLE.InternalReadOnly,
      ROLE.ExternalReadOnly,
    ],
  },
  {
    label: "List Templates",
    pathname: new RegExp(/^(\/(templates)(\/.*)?|\/)?$/),
    path: "/templates",
    access: [
      ROLE.ManageTransfer,
      ROLE.ManageExports,
      ROLE.MPIAgent,
      ROLE.InternalReadOnly,
      ROLE.ExternalReadOnly,
    ],
  },
  {
    label: "View OCA export request",
    pathname: new RegExp(/^\/request\/oca\/[^/]+\/?$/),
    path: "/request/oca",
    access: [ROLE.OCABorder],
  },
])
